import React, { useState, useRef, useCallback } from "react";
import { useSheetDrag } from "../utils/sheetDrag";
import { createPortal } from "react-dom";
import { holdSheetEnter } from "../utils/sheetEnter";
import { RotateCcw } from "lucide-react";
import { HabitIcon } from "./HabitIcon";
import { isDarkMode } from "../theme/colorEngine";
import {
  unitOf, unitDef, quickAmounts, loggedAmount, addToLog, roundAmount,
  formatAmount, dailyGoalOf, unitWord, quickLabel,
} from "../utils/habitUnits";

// Added to <head> once at load, not as a <style> per open: a style tag mounting
// with the sheet restyled the whole page in the same frame the slide started.
if (typeof document !== "undefined" && !document.getElementById("amount-sheet-css")) {
  const el = document.createElement("style");
  el.id = "amount-sheet-css";
  el.textContent = "@keyframes amountSheetUp{from{transform:translateY(100%)}to{transform:translateY(0)}}"
    + "@keyframes amountDimIn{from{opacity:0}to{opacity:1}}";
  document.head.appendChild(el);
}

// ─── ADD AMOUNT SHEET ─────────────────────────────────────────────────────────
// How a unit habit (Steps / Pages / Glasses / Time / Custom — habitUnits.js) is
// logged. A bottom sheet that slides up, styled like the Pause habit sheet
// (Jade, 2026-10-09). Layout follows the usual "log an amount" pattern
// (water/step trackers, Material/Apple sheet guidance):
//   1. the habit, with a "Mark as complete" / "Undo complete" shortcut on the
//      right (fills the day to exactly the goal, or restarts it from 0 — Jade);
//   2. today's total vs the goal, big, with a progress bar (accent colour,
//      green once the goal is met);
//   3. quick-add pills that log in one tap, and a typed amount with "Add";
//   4. the save button, which says "Save" while short of the goal and
//      "Habit completed ^^" once it's met, with an Undo icon on its right.
// Changes are a draft while it's open and are written once through
// `onLog(value)` when it closes — by the save button, a tap outside, or a swipe
// down that the sheet follows (useSheetDrag, Jade 2026-10-22; Jade 2026-10-11:
// closing without the button used to throw the draft away). An amount typed in "Other amount" but not yet added
// counts too.
//
// `habit` should already be goalOn(habit, dateStr) when logging a past day, so
// the day is shown in the unit and goal it had then.
// The progress bar's thickness: 10 CSS px rounded to whole device pixels.
const DPR = typeof window !== "undefined" ? (window.devicePixelRatio || 1) : 1;
const BAR_H = Math.round(10 * DPR) / DPR;

// Above everything on screen, the Habits "+" button (99999) included — the
// sheet can open from Habit Stats, Spaces and Clock-In too, where nothing
// hides that button for it.
export function AddAmountSheet({ habit, dateLabel, rawLog, color, onLog, onClose, zIndex = 100000 }) {
  // Dark mode: the dark greens and the dimmed accent read too faint on the dark
  // sheet, so they're lightened (Jade, 2026-10-17).
  const dk = isDarkMode();
  const DONE_INK = dk ? "#86DC74" : "#3B6D11";
  const ACCENT_INK = dk ? "color-mix(in srgb, var(--c-primary) 45%, #ffffff)" : "var(--c-text-accent)";
  const unit = unitOf(habit);
  const def = unitDef(unit);
  const goal = dailyGoalOf(habit);
  const saved = loggedAmount(habit, rawLog);
  const [current, setCurrent] = useState(saved); // the draft, written on Save
  const [typed, setTyped] = useState("");
  const history = useRef([]); // draft values before each change, for Undo

  const apply = (next) => {
    if (next === current) return;
    history.current.push(current);
    setCurrent(next);
  };
  const undo = () => {
    if (!history.current.length) return;
    setCurrent(history.current.pop());
  };
  // "Undo complete" restarts the day from 0 (Jade, 2026-10-09).
  const undoComplete = () => apply(0);
  const typedValue = roundAmount(unit, typed);
  const save = () => {
    const final = typedValue ? addToLog(habit, current, typedValue) : current;
    if (final !== saved) onLog(final);
    onClose();
  };
  const pct = Math.max(0, Math.min(1, current / goal));
  const done = current >= goal;
  const fieldUnit = unit === "time" ? "min" : unitWord(habit, 2) || "";
  const isTime = unit === "time";

  // Big number + "/ goal": "4,500" over "/ 10,000 steps"; time reads "25 min" / "/ 1h".
  const big = isTime ? formatAmount(habit, current) : formatAmount(habit, current).replace(/\s.*$/, "");
  const rest = `/ ${formatAmount(habit, goal)}`;

  // Swipe down (it follows the finger) or tap outside: slides down, then saves.
  const sheet = useSheetDrag(save);
  const { sheetRef: dragSheetRef, dimRef: dragDimRef } = sheet;
  const sheetRef = useCallback((el) => { holdSheetEnter(el); dragSheetRef(el); }, [dragSheetRef]);
  const dimRef = useCallback((el) => { holdSheetEnter(el); dragDimRef(el); }, [dragDimRef]);

  const pill = { borderRadius: 999, border: "none", fontFamily: "inherit", fontWeight: 700, cursor: "pointer" };

  return createPortal(
    <div onClick={sheet.dismiss} className="no-nav-blur" style={{ position: "fixed", inset: 0, zIndex, display: "flex", alignItems: "flex-end", justifyContent: "center", fontFamily: "'Quicksand', 'Nunito', sans-serif" }}>
      {/* "backwards": the very first frame is already the off-screen start, so the sheet never
          flashes in place before sliding (not "both": that would pin the end transform and
          stop the swipe-down drag, which writes the transform, from moving it). Held on that frame until the sheet has been
          drawn (sheetEnter.js), so the slide runs smoothly instead of during the mount. */}
      {/* The dim is its own layer and fades by OPACITY, which the GPU does without repainting.
          Fading its background-colour (the old way) repainted the whole screen on the main thread
          every frame of the slide, which is what made it lag on phones (Jade, 2026-10-16). */}
      <div aria-hidden ref={dimRef} className="sheet-enter-wait" style={{ position: "absolute", inset: 0, background: "rgba(60,30,50,0.45)", animation: "amountDimIn 0.3s ease backwards", willChange: "opacity" }} />
      <div ref={sheetRef} className="sheet-enter-wait" onClick={e => e.stopPropagation()} style={{
        position: "relative", width: "100%", maxWidth: 480, boxSizing: "border-box", background: "var(--c-bg-active)",
        borderRadius: "32px 32px 0 0", padding: "10px 22px calc(20px + env(safe-area-inset-bottom, 0px))",
        boxShadow: "0 -8px 40px var(--c-shadow)", animation: "amountSheetUp 0.3s cubic-bezier(0.22,1,0.36,1) backwards", willChange: "transform",
      }}>
        <div>
          {/* Drag handle */}
          <div style={{ width: 40, height: 4, borderRadius: 4, background: "var(--c-border)", margin: "0 auto 14px" }} />

          {/* Header: icon + name + day; on the right a one-tap "Mark as complete"
              (sets the day to exactly the goal, e.g. 10,000 steps) while short of
              it, "Undo complete" once reached (restarts the day from 0). */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 12, background: "var(--c-bg-card)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <HabitIcon h={habit} size={20} color={color} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: "var(--c-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{habit.name}</div>
              {dateLabel && <div style={{ fontSize: 12, color: "var(--c-text-soft)", fontWeight: 600 }}>{dateLabel}</div>}
            </div>
            <div style={{ flexShrink: 0 }}>
              {done ? (
                <button type="button" onClick={undoComplete}
                  style={{ border: "none", borderRadius: 999, minWidth: 0, minHeight: 0, padding: "6px 14px", fontFamily: "inherit", fontSize: 13, fontWeight: 700, cursor: "pointer",
                    background: "color-mix(in srgb, #4CAF50 16%, var(--c-bg-card))", color: DONE_INK }}>
                  ↶ Undo complete
                </button>
              ) : (
                <button type="button" onClick={() => apply(goal)}
                  style={{ border: "none", borderRadius: 999, minWidth: 0, minHeight: 0, padding: "6px 14px", fontFamily: "inherit", fontSize: 13, fontWeight: 700, cursor: "pointer",
                    background: "color-mix(in srgb, var(--c-primary) 16%, var(--c-bg-card))", color: ACCENT_INK }}>
                  ✓ Mark as complete
                </button>
              )}
            </div>
          </div>

          {/* Progress: today's total first */}
          <div style={{ marginTop: 18, display: "flex", alignItems: "baseline", justifyContent: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={{ fontSize: 34, fontWeight: 800, lineHeight: 1.1, color: done ? DONE_INK : "var(--c-text)" }}>{big}</span>
            <span style={{ fontSize: 15, fontWeight: 700, color: "var(--c-text-soft)" }}>{rest}</span>
          </div>
          {/* No layer of its own (no translateZ / will-change): on the phone the bar
              was drawn before the rest of the sheet as it slid up, showing a lone
              line for a moment (Jade, 2026-10-11). The sheet is one layer instead.
              One fixed thickness for every unit (Jade saw it vary). Measured at
              10px each time in a browser, so the likely cause is the bar landing
              between device pixels: its height is a whole number of device
              pixels. The fill gets a minimum width so a small amount
              shows as a round dot, not a squashed sliver. */}
          <div style={{ height: BAR_H, borderRadius: 999, background: "var(--c-bg-card)", marginTop: 12, overflow: "hidden" }}>
            {/* Full-width fill slid in from the left with a transform (Jade, 2026-10-11:
                the width transition stepped on phones; a transform glides on the GPU).
                max() keeps a small amount showing as a dot. */}
            <div style={{ width: "100%", height: "100%", borderRadius: 999, background: done ? "#4CAF50" : "var(--c-primary)",
              transform: pct > 0 ? `translateX(calc(max(${pct * 100}%, ${BAR_H}px) - 100%))` : "translateX(-100%)",
              transition: "transform 0.45s cubic-bezier(0.22, 1, 0.36, 1), background-color 0.3s ease" }} />
          </div>
        </div>

        {/* Quick add: one tap logs */}
        <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
          {quickAmounts(habit).map(n => (
            <button key={n} type="button" onClick={() => apply(addToLog(habit, current, n))}
              style={{ ...pill, flex: 1, padding: "12px 4px", fontSize: 15, background: "var(--c-bg-card)", color: ACCENT_INK, boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
              {quickLabel(habit, n)}
            </button>
          ))}
        </div>

        {/* Custom amount */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12 }}>
          {/* The input IS the pill (unit laid over its right end), so the app's
              global focus glow rings the whole pill instead of a box inside it. */}
          <div style={{ flex: 1, minWidth: 0, position: "relative" }}>
            <input type="number" inputMode={def.decimals ? "decimal" : "numeric"} min={0} step={def.decimals ? 0.1 : 1}
              placeholder="Other amount" value={typed} aria-label="Amount"
              onChange={e => setTyped(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && typedValue) { apply(addToLog(habit, current, typedValue)); setTyped(""); } }}
              style={{ width: "100%", boxSizing: "border-box", border: "none", borderRadius: 999, background: "var(--c-bg-card)", boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                fontFamily: "inherit", fontSize: 16, padding: `12px ${fieldUnit ? 86 : 16}px 12px 16px`, color: "var(--c-text)" }} />
            {fieldUnit && <span style={{ position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", fontSize: 13, fontWeight: 700, color: "var(--c-text-soft)", maxWidth: 70, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{fieldUnit}</span>}
          </div>
          <button type="button" disabled={!typedValue} onClick={() => { apply(addToLog(habit, current, typedValue)); setTyped(""); }}
            style={{ ...pill, padding: "12px 20px", fontSize: 15, background: "var(--c-bg-card)", color: ACCENT_INK, boxShadow: "0 1px 3px rgba(0,0,0,0.06)", opacity: typedValue ? 1 : 0.45, cursor: typedValue ? "pointer" : "default" }}>
            Add
          </button>
        </div>

        {/* Footer: the save button ("Habit completed ^^" once the goal is met), then Undo
            (icon, steps back through this opening's changes) on its right (Jade). */}
        <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
          <button type="button" onClick={save}
            style={{ ...pill, flex: 1, height: 48, padding: 0, fontSize: 15, color: "var(--c-on-primary)",
              background: "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))" }}>
            {done ? "Habit completed ^^" : "Save"}
          </button>
          <button type="button" onClick={undo} disabled={!history.current.length} aria-label="Undo" title="Undo"
            style={{ ...pill, width: 48, minWidth: 48, height: 48, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              background: "var(--c-bg-card)", color: ACCENT_INK, boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
              opacity: history.current.length ? 1 : 0.4, cursor: history.current.length ? "pointer" : "default" }}>
            <RotateCcw size={20} strokeWidth={2.4} />
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// The compact "+ 3/8" button a unit habit shows where a checkbox would be.
// Tapping it opens the Add amount sheet (it never logs by itself).
// `dashed`: a counted habit ("N times a week"), drawn with a broken border like
// every other counted habit's checkbox.
export function AmountButton({ habit, current, goal, color, onOpen, dashed = false }) {
  const c = color || "#d4789a";
  const short = unitOf(habit) === "time"
    ? `${Math.round(current)}/${Math.round(goal)}m`
    : `${fmtShort(current)}/${fmtShort(goal)}`;
  return (
    <div role="button" tabIndex={0} className="hb-amount-btn" onClick={(e) => { e.stopPropagation(); onOpen(); }}
      style={{ display: "flex", alignItems: "center", height: 28, borderRadius: 8, border: `2px ${dashed ? "dashed" : "solid"} var(--amt-c, var(--amt-c-base))`, background: "transparent", "--amt-c-base": c, cursor: "pointer", userSelect: "none", WebkitUserSelect: "none", touchAction: "manipulation", flexShrink: 0 }}>
      <span style={{ padding: "0 5px 0 7px", fontWeight: 700, fontSize: 15, color: "var(--amt-c, var(--amt-c-base))", lineHeight: "24px" }}>+</span>
      <span style={{ padding: "0 7px 0 0", fontSize: 12, fontWeight: 800, color: "var(--amt-c, var(--amt-c-base))", lineHeight: "24px", whiteSpace: "nowrap" }}>{short}</span>
    </div>
  );
}

// 4500 → "4.5k", 12000 → "12k", 8 → "8", 2.5 → "2.5" (the button has little room).
function fmtShort(n) {
  const v = Number(n) || 0;
  if (v >= 1000) return `${Math.round(v / 100) / 10}k`.replace(".0k", "k");
  return String(Math.round(v * 10) / 10);
}
